import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { isPhoneAppNow } from "../mobile/mobileMode";
import { getStationKey } from "../face/faceStation";
import useFacePin, { FACE_TIMING } from "../face/useFacePin";
import FacePreview from "../face/FacePreview";

/**
 * Imperative PIN prompt that renders a Bootstrap modal with a masked password input.
 * Returns a Promise that resolves with the trimmed PIN string, or null if cancelled.
 *
 * Usage:  const pin = await pinPrompt("Enter your 4-digit PIN:");
 *
 * `{ autofill: false }` always asks, even in an active admin session. Use it where the PIN
 * IS the authorisation (a manager releasing a weld set that is waiting on replacement
 * parts): a floor PC left in an admin session must not let anyone approve with one click.
 *
 * On a device using the phone app (src/mobile/mobileMode.js) the same prompt is a full-screen
 * PIN sheet: big masked field, number pad, full-width buttons. Desktop is unchanged.
 *
 * Face sign-in (src/face/, 2026-10-15): on a registered station computer (a live one, see
 * face/faceStation.js) the prompt also looks at the webcam; a match resolves a 16-digit face
 * token in place of the PIN, which every ordinary PIN check on the API accepts. `face`
 * defaults to `autofill`: `{ face: false }` keeps the camera off (admin unlocks, a PIN that is
 * kept after the action: the token must never outlive it), and so does `autofill: false` (the
 * PIN IS the authorisation). Never on the phone sheet or with a checkbox.
 */
export default function pinPrompt(message = "Enter your 4-digit PIN:", { autofill = true, face } = {}) {
  return pinPromptWithOptions(message, { autofill, face }).then((r) => (r ? r.pin : null));
}

/**
 * pinPrompt with an optional checkbox under the field (the admin unlock's "Remember on this
 * phone for 30 days"). Resolves {pin, checked} or null. An autofilled admin PIN resolves
 * {pin, checked: false} without asking. A face match resolves {pin: <face token>, checked: false,
 * face: {user_id, name}}.
 */
export function pinPromptWithOptions(message = "Enter your 4-digit PIN:", { autofill = true, checkbox = null, face } = {}) {
  // Auto-fill for active admin sessions
  const adminPin = sessionStorage.getItem("adminPin");
  const userRole = sessionStorage.getItem("userRole");
  if (autofill && adminPin && userRole === "admin") {
    return Promise.resolve({ pin: adminPin, checked: false });
  }

  const sheet = isPhoneAppNow();
  const faceAllowed = face === undefined ? Boolean(autofill) : Boolean(face);
  // Only a station (a key stored here) asks the API whether face is live; no key, no camera.
  const faceWanted = faceAllowed && !sheet && !checkbox && Boolean(getStationKey());
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    function cleanup() {
      root.unmount();
      container.remove();
    }

    function handleConfirm(value, checked) {
      cleanup();
      resolve(value && value.trim() ? { pin: value.trim(), checked: Boolean(checked) } : null);
    }

    function handleFace(match) {
      cleanup();
      resolve({ pin: match.token, checked: false, face: { user_id: match.user_id, name: match.name } });
    }

    function handleCancel() {
      cleanup();
      resolve(null);
    }

    // A crash inside the prompt must still let the caller's promise settle (as a cancel).
    root.render(
      <ErrorBoundary
        fallback={(error) => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: "fixed", zIndex: 2001, top: 0, left: 0, right: 0 }}>
            The PIN prompt hit an error: {(error && error.message) || String(error)}. Nothing was saved.
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={handleCancel}>Close</button>
          </div>
        )}
      >
        <PinModal
          message={message}
          checkbox={checkbox}
          sheet={sheet}
          face={faceWanted}
          onConfirm={handleConfirm}
          onFace={handleFace}
          onCancel={handleCancel}
        />
      </ErrorBoundary>
    );
  });
}

function PinModal({ message, checkbox, sheet, face = false, onConfirm, onFace, onCancel }) {
  const [show, setShow] = useState(true);
  const [value, setValue] = useState("");
  const [checked, setChecked] = useState(Boolean(checkbox && checkbox.defaultChecked));
  const inputRef = useRef(null);
  const faceState = useFacePin(face);
  const doneRef = useRef(false);
  const matched = faceState.phase === "matched" && faceState.match;

  function focusInput() {
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  }

  function confirmFace() {
    if (doneRef.current || !matched) return;
    doneRef.current = true;
    setShow(false);
    onFace(faceState.match);
  }

  // A match continues on its own after a short countdown (OK / Enter continue at once).
  useEffect(() => {
    if (!matched) return undefined;
    const t = setTimeout(confirmFace, FACE_TIMING.countdownMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched]);

  // Back to the keypad (no match, an error, the camera unavailable): the PIN field takes focus.
  useEffect(() => {
    if (faceState.phase === "fallback") focusInput();
  }, [faceState.phase]);

  useEffect(() => {
    // Auto-focus the input once the modal is shown
    const timer = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (matched && !value.trim()) {
      confirmFace();
      return;
    }
    if (doneRef.current) return;
    doneRef.current = true;
    faceState.cancel("off");
    setShow(false);
    onConfirm(value, checked);
  }

  function handleClose() {
    if (doneRef.current) return;
    doneRef.current = true;
    faceState.cancel("off");
    setShow(false);
    onCancel();
  }

  function handleChange(e) {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    // Typing a PIN ends the face attempt at once (an answer still on its way is ignored).
    if (digits && (faceState.phase === "checking" || faceState.phase === "looking" || faceState.phase === "matched")) {
      faceState.cancel("cancelled");
    }
    setValue(digits);
  }

  function handleNotMe() {
    faceState.notMe();
    focusInput();
  }

  function handleTryAgain() {
    setValue("");
    faceState.retry();
    focusInput();
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered={!sheet}
      size={sheet ? undefined : "sm"}
      fullscreen={sheet ? true : undefined}
      className={sheet ? "jq-pin-sheet" : undefined}
      style={{ zIndex: 2000 }}
      backdropClassName="pinPromptBackdrop"
    >
      {/* Keep this PIN prompt and its backdrop above any modal that opened it
          (e.g. the Pre-ship modal at z-index 1060). react-bootstrap's default
          (~1055) would otherwise render the prompt BEHIND the calling modal,
          forcing the user to close that modal to reach the PIN field. */}
      <style>{`.pinPromptBackdrop { z-index: 1999 !important; }`}</style>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>Authentication</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <label className="form-label mb-2" style={{ whiteSpace: "pre-wrap" }}>
            {message}
          </label>
          <FacePanel state={faceState} onNotMe={handleNotMe} onTryAgain={handleTryAgain} />
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            className="form-control"
            value={value}
            onChange={handleChange}
            autoComplete="off"
            placeholder="****"
            maxLength={6}
          />
          {checkbox && (
            <div className="form-check mt-3">
              <input
                id="pin-prompt-checkbox"
                type="checkbox"
                className="form-check-input"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="pin-prompt-checkbox">
                {checkbox.label}
              </label>
              {checkbox.help && <div className="form-text">{checkbox.help}</div>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size={sheet ? "lg" : "sm"} onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" size={sheet ? "lg" : "sm"} type="submit" disabled={!value.trim() && !matched}>
            OK
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

const faceStyles = {
  box: { marginBottom: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 },
  status: { fontSize: "0.9rem", color: "#6c757d" },
  matchedName: { fontSize: "1.5rem", fontWeight: 700, color: "#198754", lineHeight: 1.2, textAlign: "center" },
  countdownTrack: { width: 200, maxWidth: "100%", height: 4, background: "#e9ecef", borderRadius: 2, overflow: "hidden" },
  countdownBar: { height: "100%", background: "#198754", animation: `jqFaceCountdown ${FACE_TIMING.countdownMs}ms linear forwards` },
  fallback: { fontSize: "0.85rem", color: "#6c757d", marginBottom: 8 },
  link: { padding: 0, fontSize: "0.85rem", verticalAlign: "baseline" },
};

/** The face part of the prompt, above the PIN field (nothing at all when face is off). */
function FacePanel({ state, onNotMe, onTryAgain }) {
  const { phase, stream, match, message } = state;
  if (phase === "off" || phase === "checking") return null;

  if (phase === "fallback" || phase === "cancelled") {
    return (
      <div style={faceStyles.fallback} data-testid="face-fallback">
        {phase === "fallback" && <span>{message} </span>}
        <Button variant="link" size="sm" style={faceStyles.link} onClick={onTryAgain}>
          Try face again
        </Button>
      </div>
    );
  }

  return (
    <div style={faceStyles.box} data-testid="face-panel">
      <style>{`@keyframes jqFaceCountdown { from { width: 100%; } to { width: 0%; } }`}</style>
      {stream && <FacePreview stream={stream} width={200} />}
      {phase === "looking" && (
        <div style={faceStyles.status} role="status">Looking for you…</div>
      )}
      {phase === "matched" && match && (
        <>
          <div style={faceStyles.matchedName} role="status">✓ {match.name}</div>
          <div style={faceStyles.countdownTrack} aria-hidden="true">
            <div style={faceStyles.countdownBar} />
          </div>
          <Button variant="outline-secondary" size="sm" onClick={onNotMe}>
            Not you? Type PIN
          </Button>
        </>
      )}
    </div>
  );
}
